import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Grid, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { modalStyles } from "@/shared/styles/modalStyles";
import { FooterModalButtons } from "../../../../../../controls/buttons/FooterModalButtons";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useTorchesDataEntryActions } from "../../../hooks/useTorchesDataEntryActions";
import { Torch } from "../../../models/torches.models";

type Props = {
  show: "edit" | "new" | "";
  onHide: () => void;
  torch: Torch;
};

export const UpsertTorch = ({ show, onHide, torch }: Props) => {
  const {
    state: { errorMessage, deProcess },
    actions: { setErrorMessage },
  } = useDataEntryContext();
  const { saveTorch } = useTorchesDataEntryActions();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [weightTouched, setWeightTouched] = useState(false);

  const torchesByStrand =
    deProcess?.torches.filter((t) => t?.Strand === torch?.Strand) || [];

  const {
    handleSubmit,
    setValue,
    getValues,
    control,
    trigger,
    clearErrors,
    reset,
    watch,
  } = useForm<Torch>({
    defaultValues: {
      TotalCuts: 0,
      Undefined: 0,
      Scrapped: 0,
      Billets: 0,
    },
    mode: "onChange",
  });

  const hotLength = watch("HotLength");
  const weightOriginal = watch("WeightOriginal");
  const watchTotalCuts = watch("TotalCuts");
  const watchUndefined = watch("Undefined");
  const watchScrapped = watch("Scrapped");

  useEffect(() => {
    const totalCuts = Number(watchTotalCuts) || 0;
    const undefined = Number(watchUndefined) || 0;
    const scrapped = Number(watchScrapped) || 0;

    const calculatedBillets = Math.max(0, totalCuts - undefined - scrapped);

    setValue("Billets", calculatedBillets);
  }, [watchTotalCuts, watchUndefined, watchScrapped]);

  const onSubmit = async (data: Torch) => {
    setIsSubmitLoading(true);
    await saveTorch(data);
    setIsSubmitLoading(false);
    onHide();
  };

  useEffect(() => {
    if (show && !isSubmitLoading) {
      setWeightTouched(false);
      if (show === "edit") {
        reset(torch);
      } else {
        reset({
          Strand: torch.Strand,
          ColdLength: 0,
          HotLength: null,
          WeightRounded: 0,
          WeightOriginal: null,
        });
      }
    }
  }, [show, torch, isSubmitLoading]);

  useEffect(() => {
    if (show !== "new" || weightTouched) return;
    const hot = Number(hotLength);
    const lbsPerInch = torch?.LbsPerInch;
    if (!hot || hot <= 0 || lbsPerInch == null || lbsPerInch <= 0) {
      setValue("WeightOriginal", null);
      return;
    }
    const coldLength = Math.round(Math.round(hot) / 1.015 - 0.25);
    const computed = Math.round(coldLength * lbsPerInch * 100) / 100;
    setValue("WeightOriginal", computed);
  }, [hotLength, show, weightTouched, torch, setValue]);

  return (
    <>
      <Grid container>
        <Grid item>
          <MesfModal
            title={`${show === "new" ? "NEW" : "EDIT"} MANUAL TORCH - Strand #${torch?.Strand}`}
            open={show !== ""}
            handleClose={() => onHide()}
            id="edit-probe-modal"
            maxWidth="sm"
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <MesfModal.Content dividers sx={modalStyles.modalContent}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="HotLength"
                      control={control}
                      rules={{
                        required: "Hot Length is required",
                        pattern: {
                          value: /^\d+$/,
                          message: "Hot Length must be an integer",
                        },
                        validate: (value) => {
                          if (
                            torchesByStrand.some(
                              (t) => t.HotLength === Number(value),
                            ) &&
                            show === "new"
                          ) {
                            return "This length is already declared";
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={field.value}
                          type="number"
                          disabled={show !== "new"}
                          label="Hot Length"
                          onChange={field.onChange}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="ColdLength"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={Math.round(
                            Math.round(Number(hotLength) || 0) / 1.015 - 0.25,
                          )}
                          type="number"
                          label="Cold Length"
                          onChange={field.onChange}
                          disabled
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="WeightOriginal"
                      control={control}
                      rules={{
                        required: "Weight Original is required",
                        validate: (value) => {
                          const regex = /^\d+(\.\d{0,2})?$/;
                          if (!regex.test(String(value))) {
                            return "Number with up to 2 decimal places.";
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={field.value ?? ""}
                          type="number"
                          label="Weight Original (tons)"
                          slotProps={{
                            inputLabel: {
                              shrink:
                                field.value !== null &&
                                field.value !== undefined &&
                                String(field.value) !== ""
                                  ? true
                                  : undefined,
                            },
                          }}
                          onChange={(e) => {
                            setWeightTouched(true);
                            field.onChange(e);
                          }}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="WeightRounded"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={
                            weightOriginal !== null &&
                            weightOriginal !== undefined
                              ? Math.round(Number(weightOriginal))
                              : 0
                          }
                          type="number"
                          label="Weight Rounded (tons)"
                          onChange={field.onChange}
                          disabled
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Typography
                  variant="subtitle1"
                  style={{ marginTop: "20px", marginBottom: "-15px" }}
                >
                  Pieces
                </Typography>
              </MesfModal.Content>
              <MesfModal.Content style={{ padding: "15px 30px" }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="TotalCuts"
                      control={control}
                      rules={{
                        pattern: {
                          value: /^\d+$/,
                          message: "Total Cuts must be an integer",
                        },
                        min: {
                          value: 0,
                          message: "Total Cuts cannot be negative",
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={field.value}
                          type="number"
                          label="Total Cuts"
                          onChange={(e) => field.onChange(e)}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="Undefined"
                      control={control}
                      rules={{
                        pattern: {
                          value: /^\d+$/,
                          message: "Undefined must be an integer",
                        },
                        min: {
                          value: 0,
                          message: "Undefined cannot be negative",
                        },
                        validate: (value) => {
                          const scrappedValue = getValues("Scrapped");
                          const totalCuts = getValues("TotalCuts");
                          if (
                            (Number(value) || 0) +
                              (Number(scrappedValue) || 0) >
                            (Number(totalCuts) || 0)
                          ) {
                            return "Sum of Undefined and Scrapped cannot exceed Total Cuts";
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={field.value}
                          type="number"
                          label="Undefined"
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            trigger("Undefined");
                          }}
                          onBlur={() => clearErrors("Undefined")}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="Scrapped"
                      control={control}
                      rules={{
                        pattern: {
                          value: /^\d+$/,
                          message: "Scrapped must be an integer",
                        },
                        min: {
                          value: 0,
                          message: "Scrapped cannot be negative",
                        },
                        validate: (value) => {
                          const scrappedValue = getValues("Undefined");
                          const totalCuts = getValues("TotalCuts");
                          if (
                            (Number(value) || 0) +
                              (Number(scrappedValue) || 0) >
                            (Number(totalCuts) || 0)
                          ) {
                            return "Sum of Scrapped and Undefined cannot exceed Total Cuts";
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={field.value}
                          type="number"
                          label="Scrapped"
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            trigger("Scrapped");
                          }}
                          onBlur={() => clearErrors("Scrapped")}
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="Billets"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={field.value}
                          type="number"
                          disabled
                          label="Billets"
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </MesfModal.Content>
              <MesfModal.Actions sx={modalStyles.modalActions}>
                <FooterModalButtons
                  isSubmitLoading={isSubmitLoading}
                  onHide={onHide}
                />
              </MesfModal.Actions>
            </form>
          </MesfModal>
        </Grid>
      </Grid>
      <ErrorModal
        error={errorMessage}
        onHide={() => setErrorMessage("")}
        title="Error Editing Manual Torch"
      />
    </>
  );
};
